/**
 * The one error contract of the app.
 *
 * Engines speak their own error vocabularies (pdf.js, MuPDF, tesseract,
 * Ghostscript, fetch). Components never see those: every failure that can reach the
 * user is normalised here into a {@link ToolError} with a stable code, an i18n
 * key for the user-facing text and a "what to do next" hint key.
 *
 * Rules:
 *  - no silent catch: a `catch` either rethrows, wraps into a ToolError, or is
 *    explicitly documented as expected control flow;
 *  - raw English engine messages never reach the UI — they travel in
 *    {@link ToolError.details.engineMessage} for the save report/diagnostics.
 */

import type { MessageKey } from './i18n/tr';

export const TOOL_ERROR_CODES = [
  'unsupported',
  'password-required',
  'wrong-password',
  'range-invalid',
  'value-out-of-range',
  'selection-empty',
  'input-missing',
  'no-text',
  'no-match',
  'no-xfa',
  'no-bookmarks',
  'xfa-dynamic',
  'xfa-static',
  'password-policy',
  'encrypted-unsupported',
  'corrupt-document',
  'unsupported-format',
  'file-too-large',
  'page-limit',
  'quota-exceeded',
  'out-of-memory',
  'aborted',
  'timeout',
  'write-failed',
  'verification-failed',
  'redaction-geometry-unknown',
  'pending-redactions',
  'conflict',
  'asset-missing',
  'asset-hash-mismatch',
  'asset-offline',
  'asset-stale',
  'font-missing',
  'glyph-missing',
  'replacement-invalid',
  'ocr-language-missing',
  'voice-unavailable',
  'permission-denied',
  'pdfa-failed',
  'pdfa-not-compliant',
  'internal',
] as const;

export type ToolErrorCode = (typeof TOOL_ERROR_CODES)[number];

/** Codes whose user text is part of the product contract. */
export const TOOL_ERROR_KEYS = TOOL_ERROR_CODES.reduce<Record<ToolErrorCode, string>>(
  (keys, code) => {
    keys[code] = `error.${code}.message`;
    return keys;
  },
  {} as Record<ToolErrorCode, string>,
);

export const TOOL_ERROR_HINT_KEYS = TOOL_ERROR_CODES.reduce<Record<ToolErrorCode, string>>(
  (keys, code) => {
    keys[code] = `error.${code}.hint`;
    return keys;
  },
  {} as Record<ToolErrorCode, string>,
);

export interface ToolErrorDetails {
  /** Which engine/subsystem produced the failure (`pdfjs`, `mupdf`, `tesseract`, `ghostscript`, `fonts`, `pdf-text-engine`, `model`, `ui`, `fs`). */
  readonly engine: string;
  readonly pageIndex?: number;
  readonly path?: string;
  /** Verbatim engine text. Diagnostics only — never rendered as the user message. */
  readonly engineMessage?: string;
  /**
   * Values the localized message and hint interpolate (`{chars}`), for the codes whose
   * text is only useful when it names what failed — the user's own characters, not
   * engine text. Surfaces that translate a code pass them to `t(key, params)`.
   */
  readonly params?: Readonly<Record<string, string | number>>;
  readonly cause?: unknown;
}

export class ToolError extends Error {
  readonly code: ToolErrorCode;
  readonly details: ToolErrorDetails;

  constructor(code: ToolErrorCode, details: ToolErrorDetails, options?: { cause?: unknown }) {
    super(`[${code}] ${details.engine}${details.engineMessage ? `: ${details.engineMessage}` : ''}`, {
      cause: options?.cause ?? details.cause,
    });
    this.name = 'ToolError';
    this.code = code;
    this.details = details;
  }

  get messageKey(): MessageKey {
    return TOOL_ERROR_KEYS[this.code] as MessageKey;
  }

  get hintKey(): MessageKey {
    return TOOL_ERROR_HINT_KEYS[this.code] as MessageKey;
  }
}

export function isToolError(value: unknown): value is ToolError {
  return value instanceof ToolError;
}

/**
 * A lazy chunk the browser could not fetch — offline with the chunk not cached, or a
 * release that replaced it — in the words of Chromium, Firefox and Safari, and of the
 * bundler's CSS preload. None of it says anything about the user's document.
 */
const CHUNK_LOAD_FAILED =
  /dynamically imported module|Importing a module script failed|Unable to preload CSS/i;

/**
 * Last line of defence: never let a raw error escape to the UI layer.
 * Unknown shapes become `internal` with the engine message preserved.
 *
 * A cancellation is recognised by its name, whoever raised it: `fetch` and
 * `AbortSignal.throwIfAborted` throw a `DOMException`, while `throwIfAborted`
 * (`pdf-core/ops/types`) and the engines' own cancel paths throw a plain `Error`. The
 * name is what they share, so it is the one rule — a cancellation is never `internal`.
 */
export function toToolError(value: unknown, engine = 'model'): ToolError {
  if (isToolError(value)) return value;
  if ((value instanceof Error || value instanceof DOMException) && value.name === 'AbortError') {
    return new ToolError('aborted', { engine, engineMessage: value.message });
  }
  if (value instanceof Error && CHUNK_LOAD_FAILED.test(value.message)) {
    // A browser with a network that still cannot fetch a chunk of this build is holding a page
    // from a release that has since replaced it: "connect to the internet" would be the wrong
    // instruction there, and reloading is the right one. Offline, connecting is the cure. A
    // runtime that does not report connectivity (no `navigator`, no `onLine`) is not claimed
    // to be online.
    const online = typeof navigator !== 'undefined' && navigator.onLine === true;
    const code = online ? 'asset-stale' : 'asset-offline';
    return new ToolError(code, { engine, engineMessage: value.message }, { cause: value });
  }
  if (value instanceof Error) {
    return new ToolError('internal', { engine, engineMessage: value.message }, { cause: value });
  }
  return new ToolError('internal', { engine, engineMessage: String(value) }, { cause: value });
}
